import { defineStore } from "pinia";
import { PortalEventStats } from "./entity";
import { PortalEventStatsList } from "./list.gen";
import type {
  PortalEventStatsStore,
  PortalEventStatsStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const portalEventStatsList = new PortalEventStatsList();
export const usePortalEventStatsStore = defineStore("portalEventStatsStore", {
  state: (): PortalEventStatsStoreState => ({
    activeID: null,
    cache: {},
    list: [] as PortalEventStats[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-PortalEventStats", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.PortalEventStats"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(portalEventStats: PortalEventStats): Promise<void> {
      // Set active
      await portalEventStats
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = portalEventStats.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(portalEventStats: PortalEventStats): void {
      const id = portalEventStats.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(portalEventStats);

        // Set active
        if (this.active === null) {
          this.activeID = portalEventStats.id;
        }
      } else {
        this.reinsert(portalEventStats);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as PortalEventStats[]).map((x: any) => {
        const portalEventStats = new PortalEventStats({});
        portalEventStats.set(x as JSONData);
        return portalEventStats;
      });
    },
    async delete(portalEventStats: PortalEventStats): Promise<void> {
      await portalEventStats
        .delete()
        .then(() => {
          this.remove(portalEventStats);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const portalEventStats = (this.list as PortalEventStats[]).find(
        (x: PortalEventStats) => x.id === id,
      );
      if (portalEventStats !== undefined) {
        await this.delete(portalEventStats);
      } else {
        throw new Error("Could not find PortalEventStats with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as PortalEventStats[]).some(
        (x: PortalEventStats) => x.id === id,
      );
    },
    get(id: string): PortalEventStats | null {
      return (
        (this.list as PortalEventStats[]).find(
          (x: PortalEventStats) => x.id === id,
        ) || null
      );
    },
    reinsert(portalEventStats: PortalEventStats): void {
      const index = (this.list as PortalEventStats[]).findIndex(
        (x: PortalEventStats) => x.id === portalEventStats.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          portalEventStats,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(portalEventStats);
      }
      this.updateKey += 1;
    },
    remove({ id }: PortalEventStats): void {
      const index = (this.list as PortalEventStats[]).findIndex(
        (x: PortalEventStats) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<PortalEventStats> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const portalEventStats = this.get(id);
        if (portalEventStats !== null) {
          return portalEventStats;
        }
      }

      // Retrieve portalEventStats
      const portalEventStats = new PortalEventStats({});

      // Get portalEventStats
      await portalEventStats.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate portalEventStats
        await this.activate(entity);
      });

      return portalEventStats;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: PortalEventStats[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as PortalEventStats[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await portalEventStatsList.getAll().then(async (list) => {
        list.forEach((portalEventStats) => {
          this.reinsert(portalEventStats);
        });

        // Activate portalEventStats
        if (list.length > 0) {
          if (portalEventStatsList.active !== null) {
            this.activeID = portalEventStatsList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("portal_event_stats_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as PortalEventStats[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PortalEventStats[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map(
            (id: string) => this.get(id) as PortalEventStats,
          ),
        };
      }

      // Retrieve list
      const items = await portalEventStatsList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((portalEventStats) => {
            this.reinsert(portalEventStats);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PortalEventStats) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PortalEventStats[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map(
            (id: string) => this.get(id) as PortalEventStats,
          ),
        };
      }

      // Retrieve list
      const items = await portalEventStatsList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((portalEventStats) => {
            this.reinsert(portalEventStats);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PortalEventStats) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PortalEventStats[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map(
            (id: string) => this.get(id) as PortalEventStats,
          ),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await portalEventStatsList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((portalEventStats) => {
            this.reinsert(portalEventStats);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PortalEventStats) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(portalEventStats: PortalEventStats): Promise<string> {
      // Update portalEventStats
      await portalEventStats.update();

      // Reinsert portalEventStats
      this.reinsert(portalEventStats);

      // Activate portalEventStats
      if (this.active === null) {
        await this.activate(portalEventStats);
      }

      this.emitStoreUpdateEvent();

      return portalEventStats.id;
    },
  },
  getters: {
    active(): PortalEventStats | null {
      if (this.activeID === null) {
        return null;
      }

      const portalEventStats = (this.list as PortalEventStats[]).find(
        (x: PortalEventStats) => x.id === this.activeID,
      );
      if (portalEventStats === undefined) {
        return null;
      }

      return portalEventStats;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createPortalEventStatsStore(): PortalEventStatsStore {
  const store = usePortalEventStatsStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as PortalEventStatsStore; // TODO: Fix this
}
